import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Edit2, Plus, Target, Upload, User, Wand2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { gameService, gameResult, type Game } from '../../../services/gameService';
import { gameSubstitutionService, type GameSubstitution, quarterLabel } from '../../../services/gameSubstitutionService';
import type { DepthChartEntry } from '../../../types/depth-chart';
import type { GamePlan, Player, StatWithId, Team } from '../../../types';
import { InjuryBadge } from '../../ui/InjuryBadge';
import type { InjuryStatus } from '../../../constants/injuries';
import { GameDetailsPlayerStatsSummary } from '../GameDetailsPlayerStatsSummary';
import { AddSubstitutionModal } from '../AddSubstitutionModal';
import { PlayTracker } from './PlayTracker';
import { HudlImportModal } from '../HudlImportModal';
import { SubstitutionStrategy } from '../../gameplan/SubstitutionStrategy';
import { GamePlanCreationWizard } from '../../gameplan/GamePlanManagement';
import { GamePlanDetail } from '../../gameplan/GamePlanDetail';
import GamePlanEditModal from '../../gameplan/GamePlanEditModal';
import GamePlanRegenerateModal from '../../gameplan/GamePlanRegenerateModal';

interface FootballGameDetailProps {
  game: Game;
  gameId: string;
  currentTeam: Team;
  allTeamPlayers: Player[];
  playerCards: Map<string, string>;
  playerStats: Map<string, StatWithId[]>;
  calculatedStats: Map<string, Record<string, number>>;
  /** Game-lineup-ordered-then-roster list of players who have a player card (shared with GenericSportGameDetail). */
  recordStatsOrderedPlayers: Player[];
  expandedStatsPlayerIds: Set<string>;
  hasExistingGameStats: boolean;
  onToggleStatsExpanded: (playerId: string) => void;
  onEditStats: (playerId: string, cardId: string) => void;
  /** Re-fetch the game row (score/notes) after an update made outside this component's own score-save flow (Hudl import). */
  refreshGame: () => Promise<void>;
  /** Re-fetch per-player stats after Hudl import. */
  loadGameStats: () => Promise<void>;
}

/** Football-specific game detail: score card, tabbed substitutions/stats/game-plan/notes. */
export function FootballGameDetail({
  game,
  gameId,
  currentTeam,
  allTeamPlayers,
  playerCards,
  playerStats,
  calculatedStats,
  recordStatsOrderedPlayers,
  expandedStatsPlayerIds,
  hasExistingGameStats,
  onToggleStatsExpanded,
  onEditStats,
  refreshGame,
  loadGameStats,
}: FootballGameDetailProps) {
  const [footballTab, setFootballTab] = useState<'gameplan' | 'subs' | 'plays' | 'stats' | 'notes'>('gameplan');

  const [substitutions, setSubstitutions] = useState<GameSubstitution[]>([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [showAddSubModal, setShowAddSubModal] = useState(false);

  const [footballDepthChart, setFootballDepthChart] = useState<DepthChartEntry[]>([]);

  const [linkedGamePlan, setLinkedGamePlan] = useState<GamePlan | null>(null);
  const [linkedGamePlanLoading, setLinkedGamePlanLoading] = useState(false);
  const [showGamePlanWizard, setShowGamePlanWizard] = useState(false);
  const [gamePlanToEdit, setGamePlanToEdit] = useState<GamePlan | null>(null);
  const [showGamePlanEditModal, setShowGamePlanEditModal] = useState(false);
  const [showGamePlanRegenerateModal, setShowGamePlanRegenerateModal] = useState(false);

  const [showHudlImportModal, setShowHudlImportModal] = useState(false);

  const [showScoreEdit, setShowScoreEdit] = useState(false);
  const [scoreEditOur, setScoreEditOur] = useState('');
  const [scoreEditOpp, setScoreEditOpp] = useState('');
  const [savingScore, setSavingScore] = useState(false);

  const [notesValue, setNotesValue] = useState(game.notes ?? '');
  const [savingNotes, setSavingNotes] = useState(false);
  const notesTimerRef = useRef<number | null>(null);

  // Keep the notes textarea in sync with the loaded game (initial load, and after
  // any external refresh — e.g. Hudl import triggers refreshGame()).
  useEffect(() => {
    setNotesValue(game.notes ?? '');
  }, [game.id, game.notes]);

  const loadSubstitutions = useCallback(async () => {
    setLoadingSubs(true);
    try {
      const subs = await gameSubstitutionService.list(gameId);
      setSubstitutions(subs);
    } catch (err) {
      console.error('Error loading substitutions:', err);
    } finally {
      setLoadingSubs(false);
    }
  }, [gameId]);

  const loadFootballDepthChart = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id);
      if (!error && data) setFootballDepthChart(data);
    } catch (err) {
      console.error('Error loading football depth chart:', err);
    }
  }, [currentTeam.id]);

  const checkGamePlanColumnExists = useCallback(async (col: string): Promise<boolean> => {
    try {
      await supabase.from('game_plans').select(col).limit(1);
      return true;
    } catch (err: any) {
      if (err?.code === '42703') return false;
      throw err;
    }
  }, []);

  const loadLinkedGamePlan = useCallback(async () => {
    setLinkedGamePlanLoading(true);
    try {
      const { data, error } = await supabase
        .from('game_plans')
        .select('*')
        .eq('game_id', gameId)
        .maybeSingle();
      if (!error) setLinkedGamePlan(data ?? null);
    } catch (err) {
      console.error('Error loading linked game plan:', err);
    } finally {
      setLinkedGamePlanLoading(false);
    }
  }, [gameId]);

  const saveNotes = useCallback(
    async (value: string) => {
      setSavingNotes(true);
      try {
        await gameService.updateGame(gameId, { notes: value || null });
      } catch (err) {
        console.error('Error saving notes:', err);
        toast.error('Failed to save notes.');
      } finally {
        setSavingNotes(false);
      }
    },
    [gameId],
  );

  useEffect(() => {
    void loadSubstitutions();
    void loadFootballDepthChart();
    void loadLinkedGamePlan();
  }, [loadSubstitutions, loadFootballDepthChart, loadLinkedGamePlan]);

  const footballUnavailablePlayers = useMemo(
    () => new Set(allTeamPlayers.filter((p) => p.injury_status === 'O').map((p) => p.id)),
    [allTeamPlayers]
  );

  const handleSaveScore = async (e: React.FormEvent) => {
    e.preventDefault();
    const ourScore = scoreEditOur.trim() === '' ? null : Number(scoreEditOur);
    const oppScore = scoreEditOpp.trim() === '' ? null : Number(scoreEditOpp);
    if ((ourScore != null && !Number.isFinite(ourScore)) || (oppScore != null && !Number.isFinite(oppScore))) {
      toast.error('Please enter valid numbers');
      return;
    }
    setSavingScore(true);
    try {
      await gameService.saveScore(gameId, ourScore, oppScore);
      await refreshGame();
      setShowScoreEdit(false);
    } catch (err) {
      console.error('Error saving score:', err);
      toast.error('Failed to save score');
    } finally {
      setSavingScore(false);
    }
  };

  const ourScore = game.our_score;
  const oppScore = game.opponent_score;
  const hasScore = ourScore != null || oppScore != null;
  const scoreResult = hasScore
    ? gameResult({ our_score: ourScore ?? null, opponent_score: oppScore ?? null })
    : null;

  return (
    <div className="space-y-4">
      {/* Score card */}
      <div className="bg-white rounded-lg shadow p-3 md:max-w-lg">
        <div className="flex items-stretch border border-gray-200 rounded-md overflow-hidden text-sm">
          {scoreResult && (
            <div className={`flex items-center justify-center px-3 font-bold text-base border-r border-gray-200 ${
              scoreResult === 'W' ? 'bg-green-50 text-green-700' : scoreResult === 'L' ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-500'
            }`}>
              {scoreResult}
            </div>
          )}
          <div className="flex flex-1 divide-x divide-gray-200">
            <div className="flex flex-col items-center justify-center flex-1 py-2 px-3 min-w-0 bg-gray-50">
              <span className="text-xs text-gray-500 truncate w-full text-center leading-none mb-1">{currentTeam.name ?? 'Us'}</span>
              <span className="text-2xl font-bold tabular-nums text-gray-900 leading-none">{ourScore ?? '—'}</span>
            </div>
            <div className="flex flex-col items-center justify-center flex-1 py-2 px-3 min-w-0">
              <span className="text-xs text-gray-500 truncate w-full text-center leading-none mb-1">{game.opponent_name ?? 'Opponent'}</span>
              <span className="text-2xl font-bold tabular-nums text-gray-900 leading-none">{oppScore ?? '—'}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setScoreEditOur(ourScore != null ? String(ourScore) : '');
              setScoreEditOpp(oppScore != null ? String(oppScore) : '');
              setShowScoreEdit((v) => !v);
            }}
            className="flex items-center justify-center px-3 border-l border-gray-200 text-gray-400 hover:text-primary hover:bg-gray-50 transition"
            aria-label="Edit score"
            title="Edit score"
          >
            <Edit2 size={15} />
          </button>
        </div>

        {showScoreEdit && (
          <form onSubmit={handleSaveScore} className="mt-3 space-y-2">
            {hasExistingGameStats && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Our score is auto-derived from Hudl data. Saving here will override it.
              </p>
            )}
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">{currentTeam.name ?? 'Our Score'}</label>
                <input
                  type="number"
                  min="0"
                  value={scoreEditOur}
                  onChange={(e) => setScoreEditOur(e.target.value)}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-center tabular-nums focus:ring-2 focus:ring-primary focus:border-transparent"
                  aria-label="Our score"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">{game.opponent_name ?? 'Opp Score'}</label>
                <input
                  type="number"
                  min="0"
                  value={scoreEditOpp}
                  onChange={(e) => setScoreEditOpp(e.target.value)}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-center tabular-nums focus:ring-2 focus:ring-primary focus:border-transparent"
                  aria-label="Opponent score"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowScoreEdit(false)} className="flex-1 text-sm py-1.5 border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={savingScore} className="flex-1 text-sm py-1.5 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-60">
                {savingScore ? 'Saving…' : 'Save Score'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 gap-0 overflow-x-auto [-webkit-overflow-scrolling:touch]">
        {(
          [
            { key: 'gameplan', label: 'Game Plan' } as const,
            // Substitutions assumes a multi-player roster to sub between — a personal
            // (parent/player) account's one-player team gets a simple play tally instead.
            currentTeam.isPersonal
              ? ({ key: 'plays', label: 'Plays' } as const)
              : ({ key: 'subs', label: 'Substitutions' } as const),
            { key: 'stats', label: 'Stats' } as const,
            { key: 'notes', label: 'Notes' } as const,
          ]
        ).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setFootballTab(key)}
            className={
              'px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ' +
              (footballTab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300')
            }
            aria-selected={footballTab === key}
            role="tab"
          >
            {label}
          </button>
        ))}
      </div>

      {/* Plays tab — personal (parent/player) accounts only */}
      {footballTab === 'plays' && currentTeam.isPersonal && (
        <PlayTracker game={game} onUpdated={refreshGame} />
      )}

      {/* Substitutions tab — coach accounts only */}
      {footballTab === 'subs' && !currentTeam.isPersonal && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Substitution Strategy</h3>
            <SubstitutionStrategy
              teamId={currentTeam.id}
              currentTeam={currentTeam}
              allPlayers={allTeamPlayers}
              unavailablePlayers={footballUnavailablePlayers}
              depthChart={footballDepthChart}
              activeTab="offense"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">In-Game Substitution Log</h3>
              <button
                type="button"
                onClick={() => setShowAddSubModal(true)}
                className="btn-primary flex items-center gap-1.5 text-sm px-3 py-2"
                aria-label="Log substitution"
              >
                <Plus size={14} />
                Log Sub
              </button>
            </div>
            {loadingSubs ? (
              <div className="flex items-center justify-center h-20">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
              </div>
            ) : substitutions.length === 0 ? (
              <p className="text-sm text-gray-500">No substitutions logged yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {substitutions.map((sub) => {
                  const outPlayer = allTeamPlayers.find((p) => p.id === sub.player_out_id);
                  const inPlayer = allTeamPlayers.find((p) => p.id === sub.player_in_id);
                  return (
                    <li key={sub.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        {sub.quarter != null && (
                          <span className="shrink-0 rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs font-semibold">
                            {quarterLabel(sub.quarter)}
                          </span>
                        )}
                        <span className="text-gray-900">
                          {outPlayer
                            ? `#${outPlayer.number ?? '—'} ${outPlayer.first_name} ${outPlayer.last_name}`
                            : 'Unknown'}
                        </span>
                        <span className="text-gray-400 shrink-0">→</span>
                        <span className="text-primary font-medium">
                          {inPlayer
                            ? `#${inPlayer.number ?? '—'} ${inPlayer.first_name} ${inPlayer.last_name}`
                            : 'Unknown'}
                        </span>
                        {sub.reason && (
                          <span className="text-gray-500 truncate text-xs">{sub.reason}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await gameSubstitutionService.remove(sub.id);
                            setSubstitutions((prev) => prev.filter((s) => s.id !== sub.id));
                          } catch {
                            toast.error('Failed to remove substitution.');
                          }
                        }}
                        className="text-red-400 hover:text-red-600 shrink-0 p-1 rounded hover:bg-red-50"
                        aria-label="Remove substitution"
                      >
                        <X size={16} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Stats tab */}
      {footballTab === 'stats' && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold text-gray-900">Player Stats</h3>
            <button
              type="button"
              onClick={() => setShowHudlImportModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#e3f4f6] text-[#3da8b4] border border-[#a1d9e0] rounded-lg hover:bg-[#d5eef1] transition"
              aria-label="Import Hudl play-by-play"
            >
              <Upload size={14} />
              Import Hudl
            </button>
          </div>
          <p className="text-xs text-gray-500">Click a player to add or edit statistics for this game.</p>
          {recordStatsOrderedPlayers.length === 0 ? (
            <p className="text-sm text-gray-500">No players with player cards yet.</p>
          ) : (
            <div className="space-y-1">
              {recordStatsOrderedPlayers.map((player) => {
                const isExpanded = expandedStatsPlayerIds.has(player.id);
                const cardId = playerCards.get(player.id);
                return (
                  <div key={player.id} className="rounded border border-gray-200 bg-white overflow-hidden">
                    <div
                      className="flex items-center gap-2 p-2 w-full text-left cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={() => onToggleStatsExpanded(player.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onToggleStatsExpanded(player.id);
                        }
                      }}
                      aria-label={isExpanded ? 'Collapse player stats' : 'Expand player stats'}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {player.number != null ? (
                          <span className="font-bold text-primary text-sm min-w-[2.5rem]">#{player.number}</span>
                        ) : (
                          <User size={16} className="text-gray-400 flex-shrink-0" />
                        )}
                        <span className="font-medium text-sm text-gray-900 truncate">
                          {player.first_name ?? ''} {player.last_name ?? ''}
                        </span>
                        {player.injury_status && player.injury_status !== 'H' && (
                          <InjuryBadge
                            injuryStatus={player.injury_status as InjuryStatus}
                            size="xs"
                            showLabel={false}
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleStatsExpanded(player.id);
                        }}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary flex-shrink-0"
                        aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
                      >
                        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>
                      {cardId && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditStats(player.id, cardId);
                          }}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary flex-shrink-0"
                          aria-label="Edit stats"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="px-2 pb-2 pt-0 border-t border-gray-100 bg-gray-50/80">
                        <GameDetailsPlayerStatsSummary
                          playerId={player.id}
                          rawStats={playerStats.get(player.id) || []}
                          calculated={calculatedStats.get(player.id) || {}}
                          sport={currentTeam.sport}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Game Plan tab */}
      {footballTab === 'gameplan' && (
        <div>
          {linkedGamePlanLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
            </div>
          ) : linkedGamePlan ? (
            <div className="space-y-3">
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setGamePlanToEdit(linkedGamePlan); setShowGamePlanEditModal(true); }}
                  className="btn-secondary flex items-center gap-1.5 text-sm px-3 py-2"
                >
                  <Edit2 size={14} />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => { setGamePlanToEdit(linkedGamePlan); setShowGamePlanRegenerateModal(true); }}
                  className="btn-secondary flex items-center gap-1.5 text-sm px-3 py-2"
                >
                  <Wand2 size={14} />
                  Regenerate
                </button>
              </div>
              <GamePlanDetail
                gamePlan={{ ...linkedGamePlan, team: currentTeam }}
                allPlayers={allTeamPlayers}
                onEdit={() => { setGamePlanToEdit(linkedGamePlan); setShowGamePlanEditModal(true); }}
                onRegenerate={() => { setGamePlanToEdit(linkedGamePlan); setShowGamePlanRegenerateModal(true); }}
                hideSubstitutionStrategy
              />
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 flex flex-col items-center text-center gap-4">
              <Target size={40} className="text-gray-300" />
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">No game plan yet</h3>
                <p className="text-sm text-gray-500">Generate an AI-powered game plan for this matchup.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowGamePlanWizard(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Wand2 size={16} />
                Get Started
              </button>
            </div>
          )}
        </div>
      )}

      {/* Notes tab */}
      {footballTab === 'notes' && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Notes</h3>
            {savingNotes && (
              <span className="text-xs text-gray-500">Saving…</span>
            )}
          </div>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            rows={8}
            placeholder="Add pre-game notes, key matchups, adjustments, or post-game reflections…"
            value={notesValue}
            aria-label="Game notes"
            onChange={(e) => {
              const val = e.target.value;
              setNotesValue(val);
              if (notesTimerRef.current) window.clearTimeout(notesTimerRef.current);
              notesTimerRef.current = window.setTimeout(() => {
                void saveNotes(val);
              }, 800);
            }}
          />
        </div>
      )}

      {showAddSubModal && (
        <AddSubstitutionModal
          gameId={game.id}
          players={allTeamPlayers}
          onClose={() => setShowAddSubModal(false)}
          onAdded={async (sub) => {
            setSubstitutions((prev) => [...prev, sub]);
            setShowAddSubModal(false);
          }}
        />
      )}

      {showHudlImportModal && (
        <HudlImportModal
          gameId={gameId}
          teamId={currentTeam.id}
          players={allTeamPlayers}
          playerCards={playerCards}
          hasExistingStats={hasExistingGameStats}
          onClose={() => setShowHudlImportModal(false)}
          onImported={() => {
            setShowHudlImportModal(false);
            void loadGameStats();
            void refreshGame();
          }}
        />
      )}

      <GamePlanCreationWizard
        isOpen={showGamePlanWizard}
        onClose={() => setShowGamePlanWizard(false)}
        onSuccess={async () => {
          setShowGamePlanWizard(false);
          await loadLinkedGamePlan();
        }}
        currentTeam={currentTeam}
        allPlayers={allTeamPlayers}
        checkStatusColumnExists={() => checkGamePlanColumnExists('status')}
        checkLocationColumnExists={() => checkGamePlanColumnExists('location')}
        checkDraftDataColumnExists={() => checkGamePlanColumnExists('draft_data')}
        forceClear
        gameId={gameId}
        prefillData={{
          opponent_name: game.opponent_name ?? '',
          game_date: game.date ?? '',
          game_time: game.game_time ?? '',
          location: game.location ?? '',
          home_away: game.home_away ?? 'home',
        }}
      />

      {gamePlanToEdit && (
        <GamePlanEditModal
          isOpen={showGamePlanEditModal}
          onClose={() => { setShowGamePlanEditModal(false); setGamePlanToEdit(null); }}
          gamePlan={gamePlanToEdit}
          onSuccess={async () => {
            setShowGamePlanEditModal(false);
            setGamePlanToEdit(null);
            await loadLinkedGamePlan();
          }}
        />
      )}

      {gamePlanToEdit && (
        <GamePlanRegenerateModal
          isOpen={showGamePlanRegenerateModal}
          onClose={() => { setShowGamePlanRegenerateModal(false); setGamePlanToEdit(null); }}
          gamePlan={gamePlanToEdit}
          onSuccess={async () => {
            setShowGamePlanRegenerateModal(false);
            setGamePlanToEdit(null);
            await loadLinkedGamePlan();
          }}
        />
      )}
    </div>
  );
}
